import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { KeyRound, Truck, FileCheck2 } from "lucide-react";
import { API, IMG, RENTAL_BUDGET_OPTIONS } from "../lib/site";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHeading } from "../components/Reveal";
import { ListingCard } from "../components/ListingCard";
import { LeadForm } from "../components/LeadForm";

const FURNISHING = ["All", "Furnished", "Semi-Furnished"];

export default function Rentals() {
  const [listings, setListings] = useState([]);
  const [furnishing, setFurnishing] = useState("All");

  useEffect(() => {
    axios.get(`${API}/listings`, { params: { category: "rental" } }).then((r) => setListings(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (furnishing === "All" ? listings : listings.filter((l) => l.furnishing === furnishing)),
    [listings, furnishing]
  );

  return (
    <div data-testid="rentals-page">
      <PageHero
        overline="Rental Properties"
        title="Move in"
        accent="this weekend."
        description="Furnished and semi-furnished rentals in Gaur City, Noida Extension and Ghaziabad — transparent rents, managed handovers."
        image={IMG.rental2}
      />

      <section className="bg-white py-20 sm:py-24" data-testid="rentals-catalog-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <SectionHeading
              overline="Curated Rentals"
              title={<>Homes ready <span className="italic text-gold-600">when you are</span></>}
            />
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-2" data-testid="furnishing-filters">
                {FURNISHING.map((f) => (
                  <button
                    key={f}
                    data-testid={`furnishing-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setFurnishing(f)}
                    className={`border px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] transition-[background-color,color,border-color] duration-300 ${
                      furnishing === f
                        ? "border-navy-900 bg-navy-900 text-white"
                        : "border-navy-900/20 bg-white text-navy-900/60 hover:border-gold-500 hover:text-navy-900"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="rentals-grid">
            {filtered.map((l, i) => (
              <ListingCard key={l.id} listing={l} index={i} />
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="mt-14 border border-dashed border-navy-900/20 bg-cream p-10 text-center text-sm text-gray-500" data-testid="rentals-empty">
              No {furnishing.toLowerCase()} homes listed right now — new rentals arrive every few days. Register below for first call.
            </p>
          )}
        </div>
      </section>

      <section className="bg-cream py-24 sm:py-28" data-testid="movein-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:px-10">
          <div>
            <SectionHeading
              overline="Immediate Move-In"
              title={<>Tell us the date, <span className="italic text-gold-600">we'll hold the keys</span></>}
              description="Share your move-in window and budget. We shortlist verified rentals, arrange back-to-back viewings and handle the paperwork."
            />
            <Reveal delay={0.2}>
              <ul className="mt-9 space-y-5">
                {[
                  { icon: KeyRound, text: "Same-day keys for verified tenants on select homes" },
                  { icon: Truck, text: "Move-in coordination with society & packers" },
                  { icon: FileCheck2, text: "Rent agreement & police verification handled" },
                ].map((f, i) => (
                  <li key={i} className="flex items-center gap-4 text-[15px] text-gray-600">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-gold-500/40 bg-white">
                      <f.icon className="h-5 w-5 text-gold-600" />
                    </span>
                    {f.text}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -right-4 -top-4 h-full w-full border border-gold-500/50" aria-hidden="true" />
              <div className="relative border border-navy-900/10 bg-white p-8 shadow-[0_30px_70px_rgb(13,27,62,0.1)] sm:p-10">
                <p className="mb-6 font-serif text-2xl font-medium text-navy-900">Plan my move-in</p>
                <LeadForm
                  tid="rental-movein-form"
                  source="Rental Move-In"
                  defaultInterest="Rental Property"
                  submitLabel="Plan My Move-In"
                  budgetOptions={RENTAL_BUDGET_OPTIONS}
                  budgetLabel="Preferred Monthly Rent"
                  showMessage
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
